import { useArchive, useArchiveTerms } from "@hooks/queries"
import { defineBlock } from "eddev/blocks"
import { useEffect, useMemo, useRef, useState } from "react"
import { Icon } from "@components/ui/Icon"
import { AnimatePresence, AnimationProps, motion } from "motion/react"
import { getFadeInAnimation } from "@utils/animations"
import { useRoute, useSearchParams } from "eddev/routing"
import { ItemCard } from "@components/archive/ItemCard"
import { useTheme } from "@hooks/useTheme"
import { HighlightT } from "@utils/types"
import {
  ArchivedFilterEnum,
  ContentTypeEnum,
  RelationEnum,
  SiteFilterEnum,
  TaxonomyEnum,
  TaxQuery,
  TaxQueryField,
  TaxQueryOperator,
  UseArchiveTermsQuery,
} from "../../types.graphql"
import { Loading } from "@components/atoms/Loading"
import { ScrollSensor } from "@components/util/ScrollSensor"

export const meta: BlockMeta = {
  title: "News",
  icon: "excerpt-view",
  category: "text",
}

type LibraryParamsT = "discipline" | "experience" | "year" | "tag" | "location" | "city"

export default defineBlock("content/news", (props) => {
  const theme = useTheme()
  if (!theme) return <></>
  return <ArchiveBlock theme={theme} />
})

const ArchiveBlock = ({ theme }: { theme: "nmf" | "lcf" | "rps" | "mpv" }) => {
  const route = useRoute()
  const [searchTerm, setSearchTerm] = useState("")
  const scrollTarget = useRef<HTMLDivElement>(null!)
  const [pageCursor, setPageCursor] = useState<string>("")
  const [results, setResults] = useState<HighlightT[]>([])

  const { data: terms } = useArchiveTerms({
    contentTypes: [ContentTypeEnum.Article, ContentTypeEnum.News, ContentTypeEnum.Audio, ContentTypeEnum.Video],
    taxonomies: [
      TaxonomyEnum.Discipline,
      TaxonomyEnum.Experience,
      TaxonomyEnum.Year,
      TaxonomyEnum.City,
      TaxonomyEnum.Location,
    ],
    site:
      theme === "nmf"
        ? SiteFilterEnum.Nmf
        : theme === "mpv"
          ? SiteFilterEnum.Mpv
          : theme === "lcf"
            ? SiteFilterEnum.Lcf
            : theme === "rps"
              ? SiteFilterEnum.Rps
              : undefined,
    archived: theme === "nmf" ? ArchivedFilterEnum.True : ArchivedFilterEnum.False,
  })

  const [params, filters] = useSearchParams({
    query: "",
    discipline: [] as string[],
    experience: [] as string[],
    year: [] as string[],
    tag: [] as string[],
    location: [] as string[],
    city: [] as string[],
  })

  useEffect(() => {
    const urlQuery = route.query?.query as string | undefined
    if (urlQuery !== undefined && urlQuery !== searchTerm) {
      setSearchTerm(urlQuery)
    }
  }, [route.query?.query])

  useEffect(() => {
    setSearchTerm(params.query)
  }, [params])

  const [taxQuery, setTaxQuery] = useState<TaxQuery | null>(null)

  useEffect(() => {
    const taxArrays: NonNullable<TaxQuery["taxArray"]> = []

    // Only push Site if we actually have a theme slug
    if (theme) {
      taxArrays.push({
        taxonomy: TaxonomyEnum.Site,
        terms: [theme],
        field: TaxQueryField.Slug,
        operator: TaxQueryOperator.In,
      })
    }

    Object.entries(params).forEach(([param, terms]) => {
      const taxonomy = (() => {
        switch (param) {
          case "discipline":
            return TaxonomyEnum.Discipline
          case "experience":
            return TaxonomyEnum.Experience
          case "year":
            return TaxonomyEnum.Year
          case "tag":
            return TaxonomyEnum.Tag
          case "location":
            return TaxonomyEnum.Location
          case "city":
            return TaxonomyEnum.City
          default:
            return null
        }
      })()

      if (taxonomy && Array.isArray(terms) && terms.length > 0) {
        taxArrays.push({
          taxonomy,
          terms,
          field: TaxQueryField.Slug,
          operator: TaxQueryOperator.In,
        })
      }
    })

    setTaxQuery({
      relation: RelationEnum.And,
      taxArray: taxArrays,
    })
  }, [params])

  const filterHash = JSON.stringify(params).split(" ").join("-")

  const { data, isLoading } = useArchive({
    after: pageCursor,
    search: searchTerm,
    taxQuery: taxQuery,
    contentTypes: [ContentTypeEnum.News],
    archived: ArchivedFilterEnum.Both,
  })

  useEffect(() => {
    setResults([])
  }, [searchTerm, taxQuery])

  useEffect(() => {
    if (data?.contentNodes?.nodes) {
      setResults((prev) => [...prev, ...(data.contentNodes!.nodes as HighlightT[])])
    }
  }, [data?.contentNodes?.nodes])

  const animProps: AnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0, transition: { duration: 0.1 } },
    transition: { duration: 0.2 },
  }

  return (
    <>
      <div ref={scrollTarget} />

      {/* <div className="grid-auto grid-cols-12 gap-4 mt-5 mb-9">
        {terms?.terms?.nodes && <Filters searchTerm={searchTerm} setSearchTerm={setSearchTerm} terms={terms} />}
      </div> */}

      <div className="grid-auto grid-cols-12 gap-4 mt-5 mb-9">
        <AnimatePresence mode="wait">
          {results.length === 0 ? (
            <>
              {isLoading ? (
                <Loading animProps={animProps} />
              ) : (
                <motion.div key="noResults" className="col-span-12 text-center py-10 type-theme-title-l" {...animProps}>
                  No results found.
                </motion.div>
              )}
            </>
          ) : (
            <motion.div key={filterHash} {...animProps} className="col-span-12">
              <div className="grid-auto grid-cols-12 gap-4 mb-9">
                {results.map((card, i) => {
                  return <ItemCard key={i} item={card as HighlightT} />
                })}
              </div>

              <ScrollSensor
                onActivate={() => {
                  if (data?.contentNodes?.pageInfo.hasNextPage && data.contentNodes.pageInfo.endCursor)
                    setPageCursor(data.contentNodes.pageInfo.endCursor)
                }}
              />

              {isLoading && <Loading animProps={animProps} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

const Filters = ({
  searchTerm,
  setSearchTerm,
  terms,
}: {
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  terms: UseArchiveTermsQuery
}) => {
  const [inputText, setInputText] = useState<string>(searchTerm)

  const [params, filters] = useSearchParams({
    query: "",
    discipline: [] as string[],
    experience: [] as string[],
    year: [] as string[],
    tag: [] as string[],
    location: [] as string[],
    city: [] as string[],
  })

  const taxonomies = useMemo(() => {
    if (terms.terms?.nodes) return groupTermsByTaxonomy(terms.terms.nodes as TermNode[])
  }, [terms])

  function toggleCheckboxParam(key: LibraryParamsT, slug: string, checked: boolean) {
    const existing = params[key] ?? []
    const updated = checked ? [...existing, slug] : existing.filter((v) => v !== slug)

    filters.set(key, updated)
  }

  function handleInput(e: React.FormEvent<HTMLInputElement>) {
    const val = (e.currentTarget as HTMLInputElement).value ?? ""
    setInputText(val)
    if (val === "") {
      filters.set("query", "")
    }
  }

  return (
    <>
      <motion.div {...getFadeInAnimation(true)} className="col-span-12 md:col-span-3 flex flex-col gap-4">
        <label className="type-theme-ui-m">Search</label>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            setSearchTerm(inputText)
          }}
          className="flex flex-wrap relative gap-3 nmf-search"
        >
          <input
            type="search"
            className="text-style w-full !pl-[36px]"
            placeholder="Search..."
            value={inputText ?? ""}
            onInput={(e) => handleInput(e)}
            onChange={(e) => handleInput(e)}
          />
          <button
            type="submit"
            className="appearance-none absolute w-[32px] h-[32px] [&>svg]:w-full [&>svg]:h-full left-0 top-1/2 -translate-y-1/2 hover:text-hero"
          >
            <Icon name="search" />
          </button>
        </form>
      </motion.div>

      {taxonomies?.map((tax, i) => {
        return (
          <motion.div
            key={tax.taxonomyName}
            {...getFadeInAnimation(true)}
            className={`col-span-12 md:col-span-3 flex flex-col gap-4`}
          >
            <label className="type-theme-ui-m">{tax.taxonomyName}</label>
            <div className="flex flex-wrap relative gap-3">
              {tax.terms.map((term) => (
                <label
                  key={term.slug!}
                  htmlFor={term.slug!}
                  className="styled-chip cursor-pointer has-[:checked]:bg-fg has-[:checked]:text-bg"
                >
                  {term.name}
                  <input
                    type="checkbox"
                    id={term.slug!}
                    value={term.slug!}
                    className="hidden"
                    checked={!!params[tax.taxonomyName as LibraryParamsT]?.some((sel) => sel === term.slug)}
                    onChange={(e) => toggleCheckboxParam(tax.taxonomyName as any, term.slug!, e.currentTarget.checked)}
                  />
                </label>
              ))}
            </div>
          </motion.div>
        )
      })}
    </>
  )
}

type TermNode = { id: string; name: string; slug: string; taxonomyName: string }
type Grouped = { taxonomyName: string; terms: TermNode[] }

export function groupTermsByTaxonomy(
  nodes: TermNode[],
  opts?: {
    taxonomyOrder?: string[]
    termSort?: "name" | "slug"
    dedupeBySlug?: boolean
  },
): Grouped[] {
  const termSort = opts?.termSort ?? "name"
  const dedupe = opts?.dedupeBySlug ?? true

  // Build groups
  const groups = new Map<string, TermNode[]>()
  for (const n of nodes) {
    if (!n?.taxonomyName) continue
    if (!groups.has(n.taxonomyName)) groups.set(n.taxonomyName, [])
    const bucket = groups.get(n.taxonomyName)!
    if (!dedupe || !bucket.some((t) => t.slug === n.slug)) bucket.push(n)
  }

  // Sort terms within each group
  const termCmp =
    termSort === "slug"
      ? (a: TermNode, b: TermNode) => a.slug.localeCompare(b.slug)
      : (a: TermNode, b: TermNode) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })

  const out: Grouped[] = Array.from(groups, ([taxonomyName, terms]) => ({
    taxonomyName,
    terms: terms.sort(termCmp),
  }))

  // Order groups themselves
  if (opts?.taxonomyOrder?.length) {
    const order = new Map(opts.taxonomyOrder.map((t, i) => [t, i]))
    out.sort((a, b) => {
      const ai = order.get(a.taxonomyName) ?? Number.POSITIVE_INFINITY
      const bi = order.get(b.taxonomyName) ?? Number.POSITIVE_INFINITY
      return ai - bi || a.taxonomyName.localeCompare(b.taxonomyName)
    })
  } else {
    out.sort((a, b) => a.taxonomyName.localeCompare(b.taxonomyName))
  }

  return out
}
